/**
 * AuthContext — provee user + métodos de auth a toda la app.
 * Reemplaza el js/auth-context.js del frontend Vanilla con el patrón React.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authApi, SessionError, setSessionId } from './api';
import type { User, Role } from './types';

interface AuthContextValue {
    user: User | null;
    loading: boolean;
    error: string | null;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    async function refresh() {
        try {
            const u = await authApi.getCurrentUser();
            setUser(u);
            setError(null);
        } catch (e) {
            if (e instanceof SessionError) {
                setUser(null);
                setSessionId(null);
            } else {
                setError((e as Error).message);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { refresh(); }, []);

    const value: AuthContextValue = {
        user,
        loading,
        error,
        async login(email, password) {
            const res = await authApi.login(email, password);
            setUser(res.user);
        },
        async logout() {
            await authApi.logout();
            setUser(null);
        },
        refresh,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}

/**
 * ProtectedRoute — gate por rol. Si el user no está logueado o no tiene
 * el rol permitido, redirige a login. Mientras carga muestra un splash.
 */
interface ProtectedRouteProps {
    roles?: Role[];
    children: ReactNode;
}

export function ProtectedRoute({ roles, children }: ProtectedRouteProps) {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-brand-blue/20 border-t-brand-blue rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location.pathname }} replace />;
    }

    if (roles && !roles.includes(user.role)) {
        return <Navigate to="/login" replace />;
    }

    return <>{children}</>;
}

/**
 * Mapea rol a la home page por defecto. Mantiene la convención del Vanilla.
 */
export function homePathForRole(role: Role): string {
    switch (role) {
        case 'estudiante': return '/estudiante/dashboard';
        case 'profesor': return '/profesor/dashboard';
        case 'maestro_guia': return '/maestro-guia/dashboard';
        case 'administrador': return '/admin/dashboard';
        case 'super_admin': return '/super-admin/dashboard';
        default: return '/login';
    }
}
